import React from "react";
import Button from "./Button";

export default function Tabs({
  items = [],
  activeId,
  onChange,
  showLabel = true,
  disabled = false,
  iconOnly = false,
}) {
  const listRef = React.useRef(null);
  const visibleItems = items.filter((it) => {
    if (!it) return false;
    if (typeof it.hasContent === "boolean") return it.hasContent;
    if (typeof it.count === "number") return it.count > 0;
    if (Array.isArray(it.content)) return it.content.length > 0;
    if (Array.isArray(it.items)) return it.items.length > 0;
    if (Array.isArray(it.data)) return it.data.length > 0;
    return true;
  });
  const activeIndex = Math.max(
    0,
    visibleItems.findIndex((it) => it?.id === activeId),
  );
  const [hoverIndex, setHoverIndex] = React.useState(null);
  const effectiveIndex =
    hoverIndex !== null && hoverIndex >= 0 ? hoverIndex : activeIndex;
  const isHovering = hoverIndex !== null && hoverIndex >= 0;

  const onKeyDown = (e) => {
    if (disabled) return;
    const tabs = Array.from(
      listRef.current?.querySelectorAll('[role="tab"]') || [],
    );
    const idx = tabs.findIndex(
      (el) => el.getAttribute("data-id") === String(activeId),
    );
    const go = (nextIdx) => {
      const target = tabs[nextIdx];
      if (target) {
        const id = target.getAttribute("data-id");
        onChange?.(id);
        target.focus();
      }
    };
    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        go(Math.min(idx >= 0 ? idx + 1 : 0, tabs.length - 1));
        break;
      case "ArrowLeft":
        e.preventDefault();
        go(Math.max(idx >= 0 ? idx - 1 : 0, 0));
        break;
      case "Home":
        e.preventDefault();
        go(0);
        break;
      case "End":
        e.preventDefault();
        go(tabs.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <ul
      className={["rm-tabs", isHovering ? "is-hovering" : ""]
        .filter(Boolean)
        .join(" ")}
      role="tablist"
      aria-label="Tabs"
      ref={listRef}
      onKeyDown={onKeyDown}
      style={{
        "--tab-count": visibleItems.length || 1,
        "--tab-index": effectiveIndex < 0 ? 0 : effectiveIndex,
      }}
    >
      <span className="rm-tabs__glider" aria-hidden />
      {visibleItems.map((it) => {
        const isActive = it.id === activeId;
        const isItemDisabled = !!it.disabled || disabled;
        const badgeValue =
          it.badge ?? (typeof it.count === "number" ? it.count : null);

        const icon = it.icon;

        return (
          <li
            key={it.id}
            className={[
              "rm-tabs__item",
              badgeValue ? "rm-tabs__item_badge" : null,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <Button
              key={it.id}
              role="tab"
              data-id={it.id}
              aria-selected={isActive}
              aria-disabled={isItemDisabled}
              tabIndex={isItemDisabled ? -1 : isActive ? 0 : -1}
              className={[
                "rm-tabs__tab",
                isActive ? "is-active" : "",
                isItemDisabled ? "is-disabled" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => !isItemDisabled && onChange?.(it.id)}
              onMouseEnter={() =>
                !isItemDisabled && setHoverIndex(visibleItems.indexOf(it))
              }
              onMouseLeave={() => setHoverIndex(null)}
              disabled={isItemDisabled}
              icon={icon}
              iconOnly={iconOnly}
              label={iconOnly ? null : it.label}
            />
            {badgeValue !== null && badgeValue !== undefined ? (
              <span className="rm-tab-badge">{badgeValue}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
